import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Filter, SortAsc, SortDesc, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import MovieCard from '@/components/ui/MovieCard';
import { cn } from '@/lib/utils';
import {
  fetchTrending,
  fetchTopRated,
  fetchUpcoming,
  fetchByGenre,
  GENRES
} from '@/services/tmdb';

const MoviesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSort = searchParams.get('sort') || 'popularity';

  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [sortBy, setSortBy] = useState(initialSort); // 'popularity', 'top_rated', 'upcoming'



  // Fetch Data
  useEffect(() => {
    const loadMovies = async () => {
      setIsLoading(true);
      try {
        let data;

        // Determine fetch method based on sort/filter
        if (selectedGenre !== 'all') {
          // If genre is selected, we use discover
          // Note: fetchByGenre in tmdb.js now supports pagination
          data = await fetchByGenre(selectedGenre, page);
        } else {
          switch (sortBy) {
            case 'top_rated':
              data = await fetchTopRated(page);
              break;
            case 'upcoming':
              data = await fetchUpcoming(page);
              break;
            case 'popularity':
            default:
              data = await fetchTrending(page);
              break;
          }
        }

        setMovies(data.results);
        setTotalPages(Math.min(data.totalPages, 500)); // TMDb limit
      } catch (error) {
        console.error("Error fetching movies:", error);
      } finally {
        setIsLoading(false);
        // Scroll to top on page change
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    loadMovies();
  }, [page, selectedGenre, sortBy]);

  // Handle showing movie details
  const handleShowDetails = useCallback((movie) => {
    navigate(`/movie/${movie.id}`);
  }, [navigate]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const genres = [
    { value: 'all', label: 'Todos' },
    { value: GENRES.ACTION, label: 'Acción' },
    { value: GENRES.COMEDY, label: 'Comedia' },
    { value: GENRES.DRAMA, label: 'Drama' },
    { value: GENRES.SCIFI, label: 'Ciencia Ficción' },
    { value: GENRES.HORROR, label: 'Terror' },
    { value: GENRES.ROMANCE, label: 'Romance' },
    { value: GENRES.ANIMATION, label: 'Animación' },
  ];

  const sortOptions = [
    { value: 'popularity', label: 'Tendencias' },
    { value: 'top_rated', label: 'Mejor Valoradas' },
    { value: 'upcoming', label: 'Próximamente' },
  ];

  // Pagination Button Helper
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisibleButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage + 1 < maxVisibleButtons) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={i === page ? "default" : "outline"}
          size="icon"
          onClick={() => handlePageChange(i)}
          className={cn(
            "w-10 h-10 rounded-full",
            i === page ? "bg-primary text-primary-foreground" : "bg-secondary text-gray-400 hover:text-white"
          )}
        >
          {i}
        </Button>
      );
    }
    return buttons;
  };

  return (
    <main className="min-h-screen bg-background pt-24 lg:pt-28 pb-12">
      {/* Page Header */}
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          {/* Title */}
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Películas
            </h1>
            <p className="text-muted-foreground">
              Explora el catálogo completo
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Genre Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2 bg-secondary/50 border-border hover:bg-secondary"
                >
                  <Filter className="w-4 h-4" />
                  <span>{genres.find(g => g.value === selectedGenre)?.label || 'Género'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border-border max-h-[300px] overflow-y-auto">
                {genres.map((genre) => (
                  <DropdownMenuItem
                    key={genre.value}
                    onClick={() => {
                      setSelectedGenre(genre.value);
                      setPage(1); // Reset page on filter change
                    }}
                    className={cn(
                      'cursor-pointer',
                      selectedGenre === genre.value && 'bg-primary/10 text-primary'
                    )}
                  >
                    {genre.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sort Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2 bg-secondary/50 border-border hover:bg-secondary"
                >
                  {sortBy === 'top_rated' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                  <span>
                    {sortOptions.find((o) => o.value === sortBy)?.label}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border-border">
                {sortOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setSelectedGenre('all'); // Reset genre if sorting globally? Optional.
                      setPage(1);
                    }}
                    className={cn(
                      'cursor-pointer',
                      sortBy === option.value && 'bg-primary/10 text-primary'
                    )}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Genre Pills (Quick Access) */}
        <div className="flex flex-wrap gap-2 mb-8 pb-4 border-b border-border/50">
          {genres.slice(0, 8).map((genre) => (
            <button
              key={genre.value}
              onClick={() => {
                setSelectedGenre(genre.value);
                setPage(1);
              }}
              className={cn(
                'px-4 py-2 rounded-full text-sm font-medium transition-all duration-300',
                selectedGenre === genre.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
              )}
            >
              {genre.label}
            </button>
          ))}
        </div>

        {/* Movies Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="w-12 h-12 text-primary animate-spin" />
          </div>
        ) : movies.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 md:gap-6 min-h-[500px]">
              {movies.map((movie, index) => (
                <MovieCard
                  key={movie.id}
                  movie={movie}
                  onShowDetails={handleShowDetails}
                  index={index}
                />
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-2 mt-16 pt-8 border-t border-border/50">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="w-10 h-10 rounded-full bg-secondary text-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <div className="flex gap-2">
                {renderPaginationButtons()}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="w-10 h-10 rounded-full bg-secondary text-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Filter className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No se encontraron películas
            </h3>
            <Button
              onClick={() => {
                setSelectedGenre('all');
                setSortBy('popularity');
              }}
              variant="outline"
            >
              Reiniciar Filtros
            </Button>
          </div>
        )}
      </div>

      {/* Movie Detail Modal */}

    </main>
  );
};

export default MoviesPage;
